import Card from 'v3/components/Card';
import React, { useMemo, useState } from 'react';
import { percentage } from 'utils/number';
import { ceilDecimals } from 'v3/utils/functions';
import { convertToBRLValue, convertToDollarValue } from 'v3/utils/formatter';
import CardHeaderComponent from '../../components/CardHeaderComponent';
import HeaderTotalSummary from '../../components/HeaderTotalSummary';
import { useFreightValues } from '../../context';
import { LabelsRow } from '../../components/LabelRows';
import { FreightItemRow } from '../../components/FreightItemRow';
import ValueDetailsRow from '../../components/ValueDetailsRow';

export default function NegotiatedValuesAttendedEdit({
  defaultIsOpen = false,
  onChangeIsOpen = () => {},
}) {
  const {
    margins,
    fareCompany,
    isBRLCurrency,
    erpExchangeValue,
    advanceMoneyExternal,
    advanceMoney,
    balance,
    errors,
    onChangeAdvanceMoney,
    setAdvanceMoney,
    setTravelMargin,
    travelMargin,
    onChangeBalance,
    setBalance,
    isAttended,
  } = useFreightValues();

  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  function onToggleCard() {
    setIsOpen(!isOpen);
    onChangeIsOpen(!isOpen);
  }

  function handleChangeAdvanceMoneyPercentage(event, value) {
    if (value?.length <= 6) {
      const balanceValue = isBRLCurrency ? balance?.value : balance?.dollarValue;
      onChangeAdvanceMoney(
        balanceValue ? percentage(Number(value), Number(balanceValue)) : 0,
        value,
      );
    }
  }

  function handleChangeAdvanceMoneyValue(event, value) {
    if (value?.length <= 10) {
      let advancePercentage = 0;
      const balanceValue = Number(isBRLCurrency ? balance?.value : balance?.dollarValue) || 0;
      if (balanceValue) {
        advancePercentage = (Number(value || 0) / Number(balanceValue || 0)) * 100;
      }
      onChangeAdvanceMoney(value, advancePercentage);
    }
  }

  function onChangeAdvanceMoneyCalculatorValue(event, newValue) {
    const { value, dollarValue } = newValue;
    let advancePercentage = 0;
    const balanceValue = Number(balance?.value) || 0;
    if (balanceValue) {
      advancePercentage = (Number(value || 0) / Number(balanceValue || 0)) * 100;
    }
    setAdvanceMoney({
      value,
      dollarValue,
      percentage: advancePercentage,
    });
  }

  function handleChangeBalance(event, value) {
    if (value?.length <= 10) {
      const advancePercentage = Number(value) ? advanceMoney?.percentage : 0;
      let advanceMoneyValue = 0;
      if (advanceMoney?.percentage) {
        advanceMoneyValue = percentage(advanceMoney?.percentage, value);
      }

      const fareCompanyValue = isBRLCurrency ? fareCompany?.total : fareCompany?.totalDollarValue;

      let newMargin = 0;
      if (fareCompany?.total) {
        newMargin = ceilDecimals(
          ((Number(fareCompanyValue) - Number(value)) / Number(fareCompanyValue)) * 100,
          2,
        );
      }

      onChangeBalance(value);
      onChangeAdvanceMoney(advanceMoneyValue, advancePercentage);
      setTravelMargin(newMargin);
    }
  }

  function handleChangeBalanceCalculatorValue(event, newValue) {
    const { value, dollarValue } = newValue;
    const advancePercentage = Number(value) ? advanceMoney?.percentage : 0;
    let advanceMoneyValue = 0;
    if (advanceMoney?.percentage) {
      advanceMoneyValue = percentage(advanceMoney?.percentage, value);
    }

    let newMargin = 0;
    if (fareCompany?.total) {
      newMargin = ceilDecimals(
        ((Number(fareCompany?.total) - Number(value)) / Number(fareCompany?.total)) * 100,
        2,
      );
    }

    onChangeAdvanceMoney(advanceMoneyValue, advancePercentage);
    setTravelMargin(newMargin);
    setBalance({
      value,
      dollarValue,
    });
  }

  const bennerMargin = useMemo(() => {
    if (isBRLCurrency) {
      const bennerMarginValue = margins?.benner_margin
        ? (Number(margins?.benner_margin) / 100) * Number(fareCompany?.total)
        : 0;
      return {
        value: bennerMarginValue,
        dollarValue: convertToDollarValue(bennerMarginValue, erpExchangeValue),
        percentageValue: margins?.benner_margin,
      };
    }

    const bennerMarginValue = margins?.benner_margin
      ? (Number(margins?.benner_margin) / 100) * Number(fareCompany?.totalDollarValue)
      : 0;

    return {
      value: convertToBRLValue(bennerMarginValue, erpExchangeValue),
      dollarValue: bennerMarginValue,
      percentageValue: margins?.benner_margin,
    };
  }, [margins?.benner_margin, fareCompany, isBRLCurrency]);

  return (
    <Card containerCardBoxShadow="0px 0px 10px rgba(0, 0, 0, 0.1)">
      <CardHeaderComponent
        label="Valores negociados"
        isOpen={isOpen}
        onToggleCard={onToggleCard}
        tooltipContent="Valores negociados com transportador e cliente a partir dos valores principais"
        totalsSection={
          <>
            <HeaderTotalSummary
              label="Frete tirar margem"
              totalInReal={fareCompany?.total}
              totalInDollar={fareCompany?.totalDollarValue}
            />
            {isAttended ? (
              <HeaderTotalSummary
                label="Frete negociado"
                totalInReal={balance?.value}
                totalInDollar={balance?.dollarValue}
              />
            ) : null}
          </>
        }
      />
      {isOpen && (
        <>
          <LabelsRow />
          <ValueDetailsRow
            label="% máxima de adiantamento no ERP"
            value={advanceMoneyExternal?.value}
            dollarValue={advanceMoneyExternal?.dollarValue}
            percentageValue={advanceMoneyExternal?.percentage}
          />
          <FreightItemRow
            label="% adiantamento negociado"
            hasPercentageValue
            percentageValue={advanceMoney?.percentage || 0}
            onChangePercentageValue={handleChangeAdvanceMoneyPercentage}
            onChangeValue={handleChangeAdvanceMoneyValue}
            onChangeCalculatorValue={onChangeAdvanceMoneyCalculatorValue}
            value={advanceMoney?.value}
            dollarValue={advanceMoney?.dollarValue}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={erpExchangeValue}
            showCalculator
            labelColor="danger"
            error={errors?.advanceMoney}
            shouldDisableField={!parseFloat(balance?.value)}
            id="adiantamento-negociado"
          />
          <ValueDetailsRow
            label="Frete Tirar Margem"
            value={fareCompany?.total}
            dollarValue={fareCompany?.totalDollarValue}
            hasTooltip
            tooltipContent="Base de cálculo para Frete Tirar Margem (Valor Cliente Tomador - Desconto Insumos)"
          />
          <FreightItemRow
            label="Frete negociado *"
            onChangeValue={handleChangeBalance}
            onChangeCalculatorValue={handleChangeBalanceCalculatorValue}
            value={balance?.value}
            dollarValue={balance?.dollarValue}
            labelColor="danger"
            isBRLCurrency={isBRLCurrency}
            dollarToUse={erpExchangeValue}
            error={errors.balance}
            showCalculator
            id="frete-negociado"
          />
          <ValueDetailsRow
            label="Margem Viagem"
            value={Number(fareCompany?.total) - Number(balance?.value) || 0}
            dollarValue={Number(fareCompany?.totalDollarValue) - Number(balance?.dollarValue)}
            percentageValue={travelMargin}
            id="margem-viagem"
          />
          <ValueDetailsRow
            label="Margem ERP"
            value={bennerMargin?.value}
            dollarValue={bennerMargin.dollarValue}
            percentageValue={bennerMargin.percentageValue}
            isBRLCurrency={isBRLCurrency}
            id="margem-erp"
          />
        </>
      )}
    </Card>
  );
}
